import {
  AiOutlineArrowRight,
  AiOutlineClockCircle,
  AiOutlineUser,
} from "solid-icons/ai";
import {
  type Component,
  type ComponentProps,
  createMemo,
  Match,
  Show,
  Switch,
} from "solid-js";
import { useI18n } from "@/api/i18n";
import {
  JobCardContent,
  JobCardHeader,
  JobCard as JobCardPrimitive,
} from "@/components/shared/jobs/job-card-primitives";
import { JobOfferBadges } from "@/components/shared/jobs/job-offer-badges";
import { Badge } from "@/components/ui/badge";
import { CardTitle } from "@/components/ui/card";
import { cn } from "@/libs/cn";
import { formatDateAgo } from "@/libs/date";
import { useVisitedJobStore } from "../store/visited_job";
import type { JobOfferV2WithCompanyId } from "../types";
import {
  getJobOfferPublicBrand,
  isWorkspaceUnavailable,
} from "../utils/public-brand";

export const JobCard: Component<
  {
    job: JobOfferV2WithCompanyId;
    preview?: boolean;
    isAlreadyApplied?: boolean;
    isJobVisited?: boolean;
    onClick?: () => void;
  } & ComponentProps<"div">
> = (props) => {
  const job = props.job;

  const handleClick = () => {
    if (props.preview) {
      return;
    }
    props.onClick?.();
  };

  const { isJobVisited } = useVisitedJobStore();

  const { t } = useI18n();
  const isInactive = createMemo(
    () => job.is_active === false || isWorkspaceUnavailable(job),
  );
  const brand = createMemo(() => getJobOfferPublicBrand(job));

  return (
    <JobCardPrimitive
      class={cn(
        "border border-gray-200 shadow-none max-w-full bg-white rounded-lg w-full border-collapse relative cursor-pointer lg:mx-auto transition-shadow duration-200 hover:shadow-md",
        isInactive() &&
          "bg-muted text-muted-foreground border border-dashed border-muted opacity-70 hover:bg-muted hover:shadow-none",
      )}
      onClick={handleClick}
    >
      <JobCardHeader
        class="pb-3"
        action={
          <AiOutlineArrowRight
            size={20}
            class={cn("text-black", isInactive() && "text-muted-foreground")}
          />
        }
      >
        <div class="flex items-start gap-3 flex-1 min-w-0">
          <div class="w-12 h-12 bg-gray-200 rounded flex items-center justify-center overflow-hidden shrink-0">
            <Show
              when={brand().imageUrl}
              fallback={
                <div class="w-full h-full bg-gray-300 flex items-center justify-center text-gray-600 font-semibold text-lg">
                  {brand().fallback}
                </div>
              }
            >
              <img
                src={brand().imageUrl ?? ""}
                alt={job.title}
                class="w-full h-full object-cover"
              />
            </Show>
          </div>
          <div class="flex flex-col items-start gap-1 flex-1 min-w-0">
            <CardTitle
              class={cn(
                "text-lg font-bold text-black",
                isInactive() && "text-muted-foreground",
              )}
            >
              {job.title}
              <Show when={isInactive()}>
                <Badge variant="secondary" size="sm" class="ml-2">
                  {t("joblist.jobOffer.badge.closed")}
                </Badge>
              </Show>
            </CardTitle>
            <div class="flex items-center gap-2 flex-wrap">
              <p
                class={cn(
                  "text-sm",
                  isInactive() ? "text-muted-foreground" : "text-gray-700",
                )}
              >
                {brand().name}
              </p>
              <Show when={brand().label}>
                <Badge variant="secondary" size="sm">
                  {brand().label}
                </Badge>
              </Show>
              <div class="flex items-center gap-3 text-xs text-gray-500">
                <div class="flex items-center gap-1">
                  <AiOutlineClockCircle
                    size={14}
                    class={cn(isInactive() && "text-muted-foreground")}
                  />
                  <span>
                    {t("joblist.jobOffer.badge.date", {
                      date: formatDateAgo(job.created_at!) as string,
                    })}
                  </span>
                </div>
                <div class="flex items-center gap-1">
                  <AiOutlineUser
                    size={14}
                    class={cn(isInactive() && "text-muted-foreground")}
                  />
                  <span>{job.applications_count}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </JobCardHeader>
      <JobCardContent>
        <JobOfferBadges job={job} isInactive={isInactive} />
        <Switch>
          <Match when={props.isAlreadyApplied}>
            <div class="flex items-center w-full pt-2">
              <p class="text-xs text-primary font-semibold">
                {t("joblist.jobOffer.application.sent")}
              </p>
            </div>
          </Match>
          <Match when={!props.isAlreadyApplied && isJobVisited(job.id)}>
            <div class="flex items-center w-full pt-2">
              <p
                class={cn(
                  "text-xs font-semibold",
                  isInactive() ? "text-muted-foreground" : "text-gray-500",
                )}
              >
                {t("joblist.jobOffer.application.alreadySeen")}
              </p>
            </div>
          </Match>
        </Switch>
      </JobCardContent>
    </JobCardPrimitive>
  );
};
